// src/components/ProjectSelector/ProjectSelector.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Add, List } from '@mui/icons-material';
import { listProjects, ProjectApiError } from '../../services/projectApi';
import { ProjectsListResponse } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { isFraportUser } from '../../services/authApi';

interface ProjectSelectorProps {
  selectedProjectId: number | null;
  onProjectChange: (projectId: number) => void;
  onCreateNew?: () => void;
  showCreateButton?: boolean;
  showListButton?: boolean;
  /** When 'planning', only PLANNING projects are shown. When 'execution', only EXECUTION/COMPLETE. Omit to show all. */
  context?: 'planning' | 'execution';
}

export function ProjectSelector({
  selectedProjectId,
  onProjectChange,
  onCreateNew,
  showCreateButton = true,
  showListButton = true,
  context,
}: ProjectSelectorProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectsListResponse['projects']>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  
  // Only Fraport user type can create/manipulate projects (not Client or Tenant)
  const canCreateProjects = user && isFraportUser(user);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await listProjects({ page: 1, pageSize: 100 }); // Load first 100 projects
      setProjects(response.projects);
    } catch (err) {
      const apiError = err as ProjectApiError;
      setError(apiError.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter by context: planning shows only PLANNING; execution shows only EXECUTION/COMPLETE
  const filteredProjects =
    context === 'planning'
      ? projects.filter((p) => p.status === 'PLANNING')
      : context === 'execution'
        ? projects.filter((p) => p.status === 'EXECUTION' || p.status === 'COMPLETE')
        : projects;

  const selectedProject = filteredProjects.find(p => p.projectId === selectedProjectId);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <Autocomplete
        options={filteredProjects}
        getOptionLabel={(option) => option.name}
        value={selectedProject || null}
        onChange={(_, newValue) => {
          if (newValue) {
            onProjectChange(newValue.projectId);
          }
        }}
        loading={isLoading}
        size="small"
        sx={{ 
          minWidth: 300,
          '& .MuiInputBase-root': {
            color: 'white',
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.7)',
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white',
          },
          '& .MuiAutocomplete-popupIndicator': {
            color: 'white',
          },
          '& .MuiAutocomplete-clearIndicator': {
            color: 'white',
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Project"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress sx={{ color: 'white' }} size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {showListButton && (
        <Button
          variant="outlined"
          startIcon={<List />}
          onClick={() => navigate('/builder')}
          size="small"
        >
          All Projects
        </Button>
      )}
      {showCreateButton && canCreateProjects && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={onCreateNew || (() => navigate('/planning'))}
          size="small"
        >
          New Project
        </Button>
      )}
    </Box>
  );
}

